export enum CONVERSATION_TYPE {
  PROVIDER = 'provider_patient',
  AI_COACH = 'ai_coach',
  MANAGER_COACHING_AI = 'manager_coaching_ai',
}

export enum MESSAGE_AUTHOR_TYPE {
  PROVIDER = 'provider',
  PATIENT = 'patient',
  GUARDIAN = 'guardian',
  AI_COACH = 'ai_coach',
}

/**
 * Checks if the author type represents a message from the patient side.
 * This includes both patient and guardian (when a guardian sends a message on behalf of a dependent).
 */
export const isPatientSideAuthorType = (authorType: string | undefined | null): boolean => {
  return authorType === MESSAGE_AUTHOR_TYPE.PATIENT || authorType === MESSAGE_AUTHOR_TYPE.GUARDIAN
}

export enum CONVERSATION_STATE {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  CLOSED = 'closed',
  PENDING = 'pending',
}

export type MessageConversation = {
  conversation_attributes: {
    episode_id?: string
    last_patient_notification_sent: number
    last_provider_notification_sent: number
    unread_patient_messages_count?: number
    unread_ai_coach_messages_count?: number
    unread_ai_coach_messages_provider_count?: number
    unread_provider_messages_count?: number
    type?: CONVERSATION_TYPE
  }
  episode_ids?: string[] | null
  conversation_date_created: string
  conversation_id: string
  db_conversation_id: string | null
  patient_lyra_id: string
  provider_participant_status: string
  provider_lyra_id: string
  conversation_last_message_sent_datetime: string | null
  conversation_update_datetime?: string
  conversation_state?: CONVERSATION_STATE
  external_conversation_id?: string | null
}

export type Message = {
  attributes: string
  body: string
  date_created: string
  message_id: string
  sendStatus?: 'sent' | 'sending' | 'failed'
  conversationId?: string
}

export type ConversationMessages = {
  conversation_messages: Message[]
  next_page_token: string | null
}

export type MessageAttributes = {
  retention_status?: 'DELETED_LIVE_MESSAGE'
  metadata?: {
    linkText: string
    activity_response_id: string
    activity_id: string
  }
}

export type EssentialsMessageMetadata = {
  articleTypeLabel: string
  readTime: string
  thumbnail: string
  title: string
  uri: string
}

// Backend protobuf types for LyraLibraryResource
export enum ResourceContentFormat {
  RESOURCE_CONTENT_FORMAT_UNSPECIFIED = 0,
  RESOURCE_CONTENT_FORMAT_ARTICLE = 1,
  RESOURCE_CONTENT_FORMAT_VIDEO = 2,
  RESOURCE_CONTENT_FORMAT_AUDIO = 3,
}

export type ArticleDetails = {
  read_time?: string
}

export type VideoDetails = {
  duration?: string
}

export type AudioDetails = {
  duration?: string
}

export type LyraLibraryResource = {
  content_id: string
  title: string
  content_slug: string // Full URI path like "articles/parenting-is-a-lot"
  thumbnail_uri?: string // May be protocol-relative like "//assets.cdn..."
  content_format: ResourceContentFormat
  metadata?: Record<string, string>
  article?: ArticleDetails
  video?: VideoDetails
  audio?: AudioDetails
}

/**
 * Transforms a LyraLibraryResource from the backend into EssentialsMessageMetadata for UI consumption
 */
export const transformLyraLibraryResourceToEssentialsMetadata = (
  resource: LyraLibraryResource,
): EssentialsMessageMetadata => {
  const getArticleTypeLabel = (contentFormat: ResourceContentFormat): string => {
    switch (contentFormat) {
      case ResourceContentFormat.RESOURCE_CONTENT_FORMAT_ARTICLE:
        return 'Article'
      case ResourceContentFormat.RESOURCE_CONTENT_FORMAT_VIDEO:
        return 'Video'
      case ResourceContentFormat.RESOURCE_CONTENT_FORMAT_AUDIO:
        return 'Audio'
      default:
        return 'Article'
    }
  }

  const getReadTime = (resource: LyraLibraryResource): string => {
    switch (resource.content_format) {
      case ResourceContentFormat.RESOURCE_CONTENT_FORMAT_ARTICLE:
        return resource.article?.read_time ?? ''
      case ResourceContentFormat.RESOURCE_CONTENT_FORMAT_VIDEO:
        return resource.video?.duration ?? ''
      case ResourceContentFormat.RESOURCE_CONTENT_FORMAT_AUDIO:
        return resource.audio?.duration ?? ''
      default:
        return resource.article?.read_time ?? ''
    }
  }

  // Normalize thumbnail URI - add https: if protocol-relative, provide fallback if missing
  let normalizedThumbnail = ''
  if (resource.thumbnail_uri) {
    normalizedThumbnail = resource.thumbnail_uri.startsWith('//')
      ? `https:${resource.thumbnail_uri}`
      : resource.thumbnail_uri
  }

  return {
    title: resource.title,
    thumbnail: normalizedThumbnail,
    articleTypeLabel: getArticleTypeLabel(resource.content_format),
    readTime: getReadTime(resource),
    uri: resource.content_slug, // Already full path like "articles/parenting-is-a-lot"
  }
}
